import React from 'react';

import { Translate } from 'react-localize-redux';
import { type RouteComponentProps, Route, Switch } from 'react-router';

import LazyComponent from '@fd/ui/LazyComponent';
import Permissions from 'react-redux-permissions';

import { lazyWithRetry } from '../../../helpers/utilities';
import ErrorBoundary from '../../../layouts/Portal/ErrorBoundary';
import { RMSModules } from '../utils/rmsModules';
import { RMSSubscriptions } from '../utils/rmsSubscriptions';

type Props = RouteComponentProps;

const RMSPage = lazyWithRetry(() => import('../components/RMSPage'));
const Inventory = lazyWithRetry(() => import('./Inventory'));

const InventoryRoutes: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const {
    match: { path },
  } = props;
  return (
    <ErrorBoundary identifier="inventory">
      <Switch>
        <Route
          exact
          path={`${path}`}
          render={() => (
            <LazyComponent>
              <Inventory />
            </LazyComponent>
          )}
        />
        <Route
          exact
          path={`${path}/import-stock-items`}
          render={() => (
            <Permissions allowed={['FlipdishStaff']}>
              <LazyComponent>
                <RMSPage
                  subscription={RMSSubscriptions.INVENTORY}
                  title={<Translate id="Import_stock_items" />}
                  module={RMSModules.IMPORT_STOCK_ITEMS}
                  url={`modules/inventory/${RMSModules.IMPORT_STOCK_ITEMS}`}
                  includePropertyIdInUrl
                  allowAllProperties
                  editPermissions={['FlipdishStaff']}
                />
              </LazyComponent>
            </Permissions>
          )}
        />
        <Route
          exact
          path={`${path}/stock-items`}
          render={() => (
            <Permissions allowed={['EditInventoryStockItems', 'ViewInventoryStockItems']}>
              <LazyComponent>
                <RMSPage
                  subscription={RMSSubscriptions.INVENTORY}
                  title={<Translate id="Stock_Items" />}
                  module={RMSModules.STOCK_ITEMS}
                  url={`modules/inventory/${RMSModules.STOCK_ITEMS}`}
                  includePropertyIdInUrl
                  allowAllProperties
                  editPermissions={['EditInventoryStockItems']}
                />
              </LazyComponent>
            </Permissions>
          )}
        />
        <Route
          exact
          path={`${path}/stock-item-categories`}
          render={() => (
            <Permissions allowed={['EditInventoryStockItems', 'ViewInventoryStockItems']}>
              <LazyComponent>
                <RMSPage
                  subscription={RMSSubscriptions.INVENTORY}
                  title={<Translate id="Stock_Item_Categories" />}
                  url={`modules/inventory/${RMSModules.STOCK_ITEM_CATEGORIES}`}
                  module={RMSModules.STOCK_ITEM_CATEGORIES}
                  editPermissions={['EditInventoryStockItems']}
                />
              </LazyComponent>
            </Permissions>
          )}
        />
        <Route
          exact
          path={`${path}/suppliers`}
          render={() => (
            <Permissions allowed={['EditInventorySupplier', 'ViewInventorySupplier']}>
              <LazyComponent>
                <RMSPage
                  subscription={RMSSubscriptions.INVENTORY}
                  title={<Translate id="Suppliers" />}
                  url={`modules/inventory/${RMSModules.SUPPLIERS}`}
                  module={RMSModules.SUPPLIERS}
                  editPermissions={['EditInventorySupplier']}
                />
              </LazyComponent>
            </Permissions>
          )}
        />
        <Route
          exact
          path={`${path}/tracking-profiles`}
          render={() => (
            <Permissions
              allowed={['EditInventoryTrackingProfiles', 'ViewInventoryTrackingProfiles']}
            >
              <LazyComponent>
                <RMSPage
                  subscription={RMSSubscriptions.INVENTORY}
                  title={<Translate id="Tracking_Profiles" />}
                  url={`modules/inventory/${RMSModules.TRACKING_PROFILES}`}
                  module={RMSModules.TRACKING_PROFILES}
                  editPermissions={['EditInventoryTrackingProfiles']}
                />
              </LazyComponent>
            </Permissions>
          )}
        />
        <Route
          exact
          path={`${path}/purchase-orders`}
          render={() => (
            <Permissions allowed={['EditInventoryPurchaseOrders', 'ViewInventoryPurchaseOrders']}>
              <LazyComponent>
                <RMSPage
                  subscription={RMSSubscriptions.INVENTORY}
                  title={<Translate id="Purchase_Orders" />}
                  url={`modules/inventory/${RMSModules.PURCHASE_ORDERS}`}
                  module={RMSModules.PURCHASE_ORDERS}
                  includePropertyIdInUrl
                  editPermissions={['EditInventoryPurchaseOrders']}
                />
              </LazyComponent>
            </Permissions>
          )}
        />
      </Switch>
    </ErrorBoundary>
  );
};

export default InventoryRoutes;
